import React, { useCallback, useEffect, useMemo, useState } from "react"
import { listAllStudentHierarchyItems } from "../../../../api/studentSubjects"
import {
  DEFAULT_LEVEL_LABELS,
  enabledDepth,
  labelAt,
  normalizeLabels,
} from "../../../Subjects/detail/hierarchyUtils"
import type { HierarchyItemDto } from "../types"
import "./LearnerLevelSelectors.style.scss"

export type LevelFieldValues = {
  level_1: string
  level_2: string
  level_3: string
  level_4: string
}

export const EMPTY_LEVEL_VALUES: LevelFieldValues = {
  level_1: "",
  level_2: "",
  level_3: "",
  level_4: "",
}

const LEVEL_KEYS: (keyof LevelFieldValues)[] = [
  "level_1",
  "level_2",
  "level_3",
  "level_4",
]

interface LearnerLevelSelectorsProps {
  subjectId: string | null
  levelLabels?: string[]
  values: LevelFieldValues
  onChange: (values: LevelFieldValues) => void
  disabled?: boolean
}

/**
 * Cascading hierarchy selects — same rules as teacher LevelSelectors,
 * backed by GET /api/student/subjects/{id}/hierarchy/items.
 */
const LearnerLevelSelectors = ({
  subjectId,
  levelLabels,
  values,
  onChange,
  disabled,
}: LearnerLevelSelectorsProps) => {
  const labels = useMemo(
    () =>
      normalizeLabels(
        levelLabels && levelLabels.length ? levelLabels : DEFAULT_LEVEL_LABELS
      ),
    [levelLabels]
  )
  const depth = useMemo(() => enabledDepth(labels), [labels])
  const [optionsByLevel, setOptionsByLevel] = useState<
    Record<number, HierarchyItemDto[]>
  >({ 1: [], 2: [], 3: [], 4: [] })
  const [loadingLevel, setLoadingLevel] = useState<Record<number, boolean>>({
    1: false,
    2: false,
    3: false,
    4: false,
  })

  const loadLevel = useCallback(
    async (level: number, parentId: string | null) => {
      if (!subjectId || level > depth) return
      if (level > 1 && !parentId) {
        setOptionsByLevel((prev) => ({ ...prev, [level]: [] }))
        return
      }
      setLoadingLevel((prev) => ({ ...prev, [level]: true }))
      try {
        const items = await listAllStudentHierarchyItems(subjectId, {
          level,
          parent_id: parentId,
        })
        setOptionsByLevel((prev) => ({ ...prev, [level]: items }))
      } catch {
        setOptionsByLevel((prev) => ({ ...prev, [level]: [] }))
      } finally {
        setLoadingLevel((prev) => ({ ...prev, [level]: false }))
      }
    },
    [subjectId, depth]
  )

  useEffect(() => {
    setOptionsByLevel({ 1: [], 2: [], 3: [], 4: [] })
    if (subjectId) loadLevel(1, null)
  }, [subjectId, loadLevel])

  useEffect(() => {
    if (!subjectId) return
    for (let level = 2; level <= depth; level += 1) {
      const parentKey = LEVEL_KEYS[level - 2]
      const parentId = values[parentKey]
      loadLevel(level, parentId || null)
    }
  }, [
    subjectId,
    depth,
    values.level_1,
    values.level_2,
    values.level_3,
    loadLevel,
  ])

  const handleChange = (level: number, value: string) => {
    const next = { ...values }
    next[LEVEL_KEYS[level - 1]] = value
    for (let i = level; i < 4; i += 1) {
      next[LEVEL_KEYS[i]] = ""
    }
    onChange(next)
  }

  if (!subjectId || depth < 1) {
    return (
      <p className="learner-level-selectors__hint">
        Select a subject to filter by topic hierarchy.
      </p>
    )
  }

  return (
    <div className="learner-level-selectors">
      {LEVEL_KEYS.slice(0, depth).map((key, index) => {
        const level = index + 1
        const parentOk = level === 1 || !!values[LEVEL_KEYS[level - 2]]
        const label = labelAt(labels, level)
        return (
          <label key={key} className="learner-level-selectors__field">
            <span>{label}</span>
            <select
              value={values[key]}
              disabled={disabled || !parentOk || loadingLevel[level]}
              onChange={(e) => handleChange(level, e.target.value)}
            >
              <option value="">
                {loadingLevel[level] ? `Loading ${label}…` : `All ${label}s`}
              </option>
              {(optionsByLevel[level] || []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        )
      })}
    </div>
  )
}

export default LearnerLevelSelectors
